import React, { useContext, useEffect, useRef, useState } from 'react';
import { updateFloatDigits } from '../../utils/updateFloatDigits';
import { convertPercentToValue, convertValueToPercent } from '../../utils/convertToValue';
import CurrencyInput from '../../utils/currencyInput';
import BookerContext from '../../context/bookerContext';
import FetchService from '../../services/fetchService';
import urlService from '../../services/urlService';
import { mixpanelEventTrack } from '../../services/mixpanel';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { isNullValue, isUndefinedValue } from '../../utils/valueType';
import '../../assets/styles/tips-details.sass';
import BookerShortContext from '../../context/bookerShortContext';

const TipDetails = () => {
  const context = useContext(BookerContext);
  const { toggleTips, setIsOpenTips } = useContext(BookerShortContext);
  const {
    state: {
      fare: { total_amount } = {},
      rideId,
      tipsValue: { tips_value = null, tips_type = '' } = {},
      isWebBooker,
      defaultTips: { default_tip = null, default_tip_type = '', default_selected = false } = {},
    },
    updateTips,
    updateTipsValue,
    updateDefaultTips,
  } = context;

  const DEFAULT_TIPS_AMOUNT = 15;
  const DEFAULT_PERCENT_TIPS_AMOUNT = 20;
  const MAX_TIPS_AMOUNT = 40;
  const MAX_PERCENT_TIPS_AMOUNT = 99;

  const percentItems = [
    { id: 0, percent: 20 },
    { id: 1, percent: 25 },
    { id: 2, percent: 30 },
  ];

  const currencyItems = [
    { id: 0, name: '%', config: 'percent' },
    { id: 1, name: '$', config: 'dollar' },
  ];

  const totalAmount = total_amount ? total_amount / 100 : 0;
  const convertedValue = percent => convertPercentToValue(totalAmount, percent);

  const [tipsAmount, setTipsAmount] = useState(DEFAULT_TIPS_AMOUNT);
  const [tipsPercentAmount, setTipsPercentAmount] = useState(DEFAULT_PERCENT_TIPS_AMOUNT);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currencyIndex, setCurrencyIndex] = useState(0);
  const [isCurrencySwitched, setIsCurrencySwitched] = useState(false);
  const [error, setError] = useState(false);
  const [isOpenCustomTip, setIsOpenCustomTip] = useState(false);
  const [inputDollarValue, setInputDollarValue] = useState(DEFAULT_TIPS_AMOUNT.toString());
  const [inputPercentValue, setInputPercentValue] = useState(DEFAULT_PERCENT_TIPS_AMOUNT.toString());
  const [currentCurrency, setCurrentCurrency] = useState(currencyItems[0]);

  const tipToggler = useRef(null);
  const isShowingAmountUnderPercent = rideId && !default_selected ? 'amount-under-percent' : '';

  useEffect(() => {
    const isNotTipsValue = isUndefinedValue(tips_value) || isNullValue(tips_value);
    let defaultRideTipsValue = isNotTipsValue ? DEFAULT_PERCENT_TIPS_AMOUNT : tips_value;
    let isDefaultRideAmount = tips_type === 'amount';
    const defaultRideTips = percentItems.find(item => item.percent === defaultRideTipsValue);

    const defaultUserTips = isWebBooker && percentItems.find(item => item.percent === default_tip);
    const isDefaultUserAmount = default_tip_type === 'amount';

    if (isWebBooker && rideId && isNotTipsValue) {
      updateTipsValue({
        tips_value: default_tip,
        tips_type: default_tip_type,
      });
    }

    if (rideId && !isNotTipsValue && tips_type) {
      setTipsAmount(isDefaultRideAmount ? tips_value : convertValueToPercent(totalAmount, tips_value));
      setTipsPercentAmount(isDefaultRideAmount ? convertPercentToValue(totalAmount, tips_value) : tips_value);
      setCurrentIndex(defaultRideTips?.id);
      setCurrencyIndex(isDefaultRideAmount ? 1 : 0);
      setIsCurrencySwitched(isDefaultRideAmount ? 1 : 0);
      setIsOpenCustomTip(!defaultRideTips);
      setInputPercentValue(isDefaultRideAmount ? convertPercentToValue(totalAmount, tips_value) : tips_value);
      setInputDollarValue(isDefaultRideAmount ? tips_value : convertValueToPercent(totalAmount, tips_value));
      setCurrentCurrency(currencyItems[isDefaultRideAmount ? 1 : 0]);
    }

    if (default_selected) {
      setTipsAmount(isDefaultUserAmount ? default_tip : 0);
      setTipsPercentAmount(isDefaultUserAmount ? 0 : default_tip);
      setCurrentIndex(defaultUserTips?.id);
      setCurrencyIndex(isDefaultUserAmount ? 1 : 0);
      setIsCurrencySwitched(isDefaultUserAmount ? 1 : 0);
      setIsOpenCustomTip(!defaultUserTips);
      setInputPercentValue(isDefaultUserAmount ? 0 : default_tip);
      setInputDollarValue(isDefaultUserAmount ? default_tip : 0);
      setCurrentCurrency(currencyItems[isDefaultUserAmount ? 1 : 0]);
    }
  }, [updateTipsValue, tips_value, tips_type]);

  const config = {
    percent: {
      suffix: '%',
      decimalScale: 2,
      placeholder: '0%',
      inputMode: 'numeric',
      maxLength: 3,
    },
    dollar: {
      prefix: '$',
      decimalScale: 2,
      decimalSeparator: '.',
      fixedDecimalScale: true,
      allowEmptyFormatting: true,
      placeholder: '$0.00',
      inputMode: 'decimal',
      maxLength: 6,
    },
  };

  const isExceedsMaxAmount = (isPercent, pValue, dValue) => {
    return isPercent
      ? Math.ceil(parseInt(pValue)) > MAX_PERCENT_TIPS_AMOUNT
      : Math.ceil(parseFloat(dValue)) > MAX_TIPS_AMOUNT;
  };

  const getSelectedCurrencyItem = id => {
    const selectedId = id || id === 0 ? id : currencyIndex;
    return currencyItems.find(item => item.id === selectedId);
  };

  const selectPredefinedTipsInPercent = selectedId => {
    const item = percentItems.find(item => item.id === selectedId);
    const { id, percent } = item;
    const value = convertedValue(percent);

    setIsOpenCustomTip(false);
    setCurrentIndex(id);
    setCurrencyIndex(0);
    setCurrentCurrency(currencyItems[0]);
    setTipsAmount(value);
    setTipsPercentAmount(percent);
    setInputDollarValue(value);
    setInputPercentValue(percent);
    setError(false);
  };

  const selectCurrency = selectedId => {
    const currCurrency = getSelectedCurrencyItem(selectedId);
    const { id } = currCurrency;

    setError(isExceedsMaxAmount(id === 0, inputPercentValue, inputDollarValue));
    setIsCurrencySwitched(currencyIndex !== id);
    setCurrencyIndex(id);
    setCurrentCurrency(currCurrency);
  };

  const toggleCustomTip = () => {
    setCurrentIndex(null);
    setIsOpenCustomTip(!isOpenCustomTip);

    const { name } = getSelectedCurrencyItem();
    const isPercent = name === '%';
    let value = null;
    let formattedValue = null;
    let formattedToPercentValue = null;

    if (default_selected) {
      formattedValue = inputDollarValue;
      formattedToPercentValue = inputPercentValue;
    } else {
      if (isPercent) {
        value = inputPercentValue;
        formattedValue = updateFloatDigits(convertPercentToValue(totalAmount, parseInt(value)));
        formattedToPercentValue = inputPercentValue;
      } else {
        value = inputDollarValue;
        formattedValue = updateFloatDigits(value);
        formattedToPercentValue = updateFloatDigits(convertValueToPercent(totalAmount, parseFloat(value)));
      }
    }

    setError(isExceedsMaxAmount(isPercent, inputPercentValue, inputDollarValue));
    setTipsAmount(formattedValue);
    setTipsPercentAmount(formattedToPercentValue);
    setInputPercentValue(formattedToPercentValue);
    setInputDollarValue(formattedValue);

    mixpanelEventTrack(isWebBooker ? 'customTipSelectedWebBook' : 'customTipSelected');
  };

  const keyUpInputHandler = () => {
    const { name } = getSelectedCurrencyItem();
    const isPercent = name === '%';

    let percentValue, dollarValue;

    if (default_selected) {
      percentValue = inputPercentValue;
      dollarValue = inputDollarValue;
    } else {
      if (isPercent) {
        percentValue = parseInt(inputPercentValue);
        dollarValue = convertPercentToValue(totalAmount, percentValue);
      } else {
        dollarValue = parseFloat(inputDollarValue);
        percentValue = convertValueToPercent(totalAmount, dollarValue);
      }
    }

    setError(isExceedsMaxAmount(isPercent, percentValue, dollarValue));
  };

  const focusInputHandler = () => {
    const { name } = getSelectedCurrencyItem();
    const isPercent = name === '%';

    const value = isPercent ? parseInt(inputPercentValue) : parseFloat(inputDollarValue);
    if (value === 0) {
      isPercent ? setInputPercentValue('') : setInputDollarValue('');
    }
  };

  const blurInputHandler = () => {
    const { name } = getSelectedCurrencyItem();
    const isPercent = name === '%';

    let value = isPercent ? parseInt(inputPercentValue) : parseFloat(inputDollarValue);
    if (!value) value = 0;

    const convertedValue = isPercent
      ? convertPercentToValue(totalAmount, value)
      : convertValueToPercent(totalAmount, value);

    let inputPercentValueUpdated = isPercent ? value : convertedValue;
    let inputDollarValueUpdated = isPercent ? convertedValue : updateFloatDigits(value);

    if (default_selected) {
      inputPercentValueUpdated = inputPercentValue;
      inputDollarValueUpdated = inputDollarValue;
    }

    setInputPercentValue(inputPercentValueUpdated);
    setInputDollarValue(inputDollarValueUpdated);
    setTipsAmount(inputDollarValueUpdated);
    setTipsPercentAmount(inputPercentValueUpdated);
    return [inputDollarValueUpdated, inputPercentValueUpdated];
  };

  const changeInputHandler = v => {
    const { name } = getSelectedCurrencyItem();
    const isPercent = name === '%';

    const { floatValue } = v;
    isPercent ? setInputPercentValue(floatValue) : setInputDollarValue(floatValue);
  };

  const clearIsCurrencySwitched = () => {
    setIsCurrencySwitched(false);
  };

  const updateTipAmount = nextState => {
    if (!error) {
      if (default_selected) {
        const isDollarSelected = isOpenCustomTip && currentCurrency.config === 'dollar';
        const amountValue = isDollarSelected ? parseFloat(tipsAmount) * 100 : tipsPercentAmount;
        const currencyValue = isDollarSelected ? 'amount' : 'percent';

        FetchService.getWebApiResource(urlService.updateUserTips(amountValue, currencyValue))
          .then(response => {
            updateDefaultTips({
              default_tip: currencyValue === 'amount' ? amountValue / 100 : amountValue,
              default_tip_type: currencyValue,
              default_selected: false,
            });

            console.log('tips have been saved');
          })
          .catch(error => {
            console.log('error with saving tips');
          });
      } else {
        const updateTipsHandler = (amount, percent) => {
          const isDollarSelected = isOpenCustomTip && currentCurrency.config === 'dollar';
          const amountValue = isDollarSelected ? parseFloat(tipsAmount) * 100 : tipsPercentAmount;
          const tipPercent = isDollarSelected ? null : amountValue;
          const tipAmount = isDollarSelected ? amountValue : null;

          updateTips(amount === null ? tipsAmount : amount);
          updateTipsValue({
            tips_value: isDollarSelected ? updateFloatDigits(tipAmount / 100) : tipPercent,
            tips_type: isDollarSelected ? 'amount' : 'percent',
          });
          setIsOpenTips(false);

          const tipsParam = isDollarSelected ? { tip_amount: tipAmount } : { tip_percent: tipPercent };
          FetchService.getWebApiResource(urlService.updateTips(rideId, tipsParam))
            .then(response => {
              console.log('tips have been saved');
            })
            .catch(error => {
              console.log('error with saving tips');
            });
        };

        let amount = null,
          percent = null;
        if (isOpenCustomTip) [amount, percent] = blurInputHandler();
        updateTipsHandler(amount, percent);
      }
    }

    toggleTips(nextState);
  };

  const closeTipAmount = () => {
    setIsOpenTips(false);
    toggleTips(false);
  };

  useEffect(() => {
    const onKeyDownHandler = event => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter' || event.keyCode === 13) {
        event.preventDefault();
        updateTipAmount();
      }
    };
    document.addEventListener('keydown', onKeyDownHandler);

    return () => {
      document.removeEventListener('keydown', onKeyDownHandler);
    };
  }, [isOpenCustomTip, inputPercentValue, inputDollarValue]);

  const [setIsZoneOpened, setSwipedZone] = useSwipeGesture({
    zone: tipToggler?.current,
    exceptionZoneList: {
      custom: ['toggle-btn'],
    },
    toggleHandler: closeTipAmount,
  });

  useEffect(() => {
    if (!tipToggler?.current) return;
    setSwipedZone(tipToggler?.current);
  }, [tipToggler?.current, setSwipedZone]);

  return (
    <div className="overlay">
      <div className="tip-container">
        <div ref={tipToggler} className="toggle-btn" onClick={closeTipAmount} />
        <div className="tip-details clearfix">
          {/*<p className={'r-Label'}>Your Tip</p>*/}
          {/*<span className={'total-tip-amount'}>${tipsAmount}</span>*/}
        </div>
        <div className="tip-list-container">
          <ul className="clearfix">
            {percentItems.map(item => (
              <li
                key={item.id}
                onClick={() => selectPredefinedTipsInPercent(item.id)}
                className={`${
                  currentIndex === item.id && !isOpenCustomTip ? 'active' : ''
                } ${isShowingAmountUnderPercent}`}
              >
                <span className="t-percent">{item.percent}%</span>
                <span className="t-value">${convertedValue(item.percent)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="tip-custom-container">
          {!isOpenCustomTip && (
            <div className="custom-button text-center" onClick={toggleCustomTip}>
              Custom Tip
            </div>
          )}

          {isOpenCustomTip && (
            <div className="custom-input-field">
              <form id="tip-form" className="form-horizontal">
                <div className="form-inner-container">
                  <div className={`form-group${error ? ' error-group' : ''}`}>
                    <label htmlFor="cc-tip" className="form-label">
                      Custom Tip
                    </label>
                    <CurrencyInput
                      maskOptions={config[currentCurrency.config]}
                      type="text"
                      id="cc-tip"
                      className={`form-input currency-${currentCurrency.config}`}
                      autoComplete="off"
                      isCurrencySwitched={isCurrencySwitched}
                      inputValue={currencyIndex === 0 ? inputPercentValue : inputDollarValue}
                      onChange={changeInputHandler}
                      onFocus={focusInputHandler}
                      onBlur={blurInputHandler}
                      onKeyUp={keyUpInputHandler}
                      clearIsCurrencySwitched={clearIsCurrencySwitched}
                    />
                    {error && <p className="error">Exceeds the maximum allowed amount.</p>}
                  </div>
                  <div className="currency-group">
                    <ul className="clearfix">
                      {currencyItems.map(item => (
                        <li
                          key={item.id}
                          onClick={() => selectCurrency(item.id)}
                          className={currencyIndex === item.id ? 'active' : ''}
                        >
                          <span className="t-currency">{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
        <button className="apply-btn btn btn-view btn-primary" onClick={updateTipAmount}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default TipDetails;
